import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  useIntegrationSettings,
  useUpdateIntegrationSettings,
  useDeleteIntegrationSettings,
} from '../../../../api/hooks/settings.hook';
import type { IntegrationSettingsEntry } from '../../../../api/services/settings';
import { JsonEditor } from '../../../../components/common/JsonEditor';
import { formatDateTime } from './shared';

type IntegrationCardProps = {
  entry: IntegrationSettingsEntry;
  onSave: (integration: string, config: Record<string, unknown>) => Promise<void>;
  onDelete: (integration: string) => Promise<void>;
};

const IntegrationCard: React.FC<IntegrationCardProps> = ({ entry, onSave, onDelete }) => {
  const [editorValue, setEditorValue] = useState(
    JSON.stringify(entry.config ?? {}, null, 2) || '{}',
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setEditorValue(JSON.stringify(entry.config ?? {}, null, 2) || '{}');
  }, [entry.config]);

  const secretKeys = Object.entries(entry.secret_fields ?? {})
    .filter(([, present]) => Boolean(present))
    .map(([key]) => key);

  const handleSave = async () => {
    try {
      const parsed = JSON.parse(editorValue || '{}');
      await onSave(entry.integration, parsed);
      setError(null);
      toast.success(`Saved configuration for ${entry.integration}.`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to save integration.';
      setError(message);
      toast.error(message);
    }
  };

  const handleDelete = async () => {
    try {
      await onDelete(entry.integration);
      toast.success(`Removed ${entry.integration} integration.`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to delete integration.';
      toast.error(message);
    }
  };

  return (
    <div className="border border-neutral-200 rounded-lg overflow-hidden shadow-sm">
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between bg-neutral-50 px-4 py-3 border-b border-neutral-200">
        <div>
          <h3 className="text-sm font-semibold text-neutral-900 capitalize">
            {entry.integration.replace(/_/g, ' ')}
          </h3>
          <p className="text-xs text-neutral-500">
            Last updated: {formatDateTime(entry.updated_at)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="text-xs font-medium text-red-600 hover:text-red-700"
            onClick={handleDelete}
          >
            Delete
          </button>
        </div>
      </div>
      <div className="p-4 space-y-3">
        <JsonEditor value={editorValue} onChange={setEditorValue} height={220} />
        {secretKeys.length > 0 ? (
          <p className="text-xs text-neutral-500">
            Stored secrets are hidden. Provide new values to replace: {secretKeys.join(', ')}
          </p>
        ) : null}
        {error ? <p className="text-xs text-red-600">{error}</p> : null}
        <div className="flex justify-end">
          <button
            type="button"
            className="inline-flex items-center rounded-md bg-neutral-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-neutral-800 focus:outline-none focus:ring-2 focus:ring-neutral-500"
            onClick={handleSave}
          >
            Save integration
          </button>
        </div>
      </div>
    </div>
  );
};

export const IntegrationsConfiguration = () => {
  const integrations = useIntegrationSettings();
  const updateIntegration = useUpdateIntegrationSettings();
  const deleteIntegration = useDeleteIntegrationSettings();
  const [newName, setNewName] = useState('');
  const [newConfig, setNewConfig] = useState('{\n  \n}');
  const [newError, setNewError] = useState<string | null>(null);

  const handleSaveExisting = async (
    integration: string,
    config: Record<string, unknown>,
  ): Promise<void> => {
    await updateIntegration.mutateAsync({ integration, config });
  };

  const handleDelete = async (integration: string): Promise<void> => {
    await deleteIntegration.mutateAsync(integration);
  };

  const handleCreate = async () => {
    const trimmed = newName.trim();
    if (!trimmed) {
      toast.error('Enter an integration identifier before saving.');
      return;
    }
    try {
      const parsed = JSON.parse(newConfig || '{}');
      await updateIntegration.mutateAsync({ integration: trimmed, config: parsed });
      setNewName('');
      setNewConfig('{\n  \n}');
      setNewError(null);
      toast.success(`Added configuration for ${trimmed}.`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to add integration.';
      setNewError(message);
      toast.error(message);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-8 space-y-4 sm:space-y-6">
        <div className="space-y-1">
          <h1 className="text-xl sm:text-2xl font-semibold text-neutral-900">
            Integration credentials
          </h1>
          <p className="text-xs sm:text-sm text-neutral-600">
            Manage API keys and tokens used by action adapters.
          </p>
        </div>
        <section className="bg-white rounded-xl shadow-sm border border-neutral-200 p-4 sm:p-6 space-y-4">
          <div className="space-y-6">
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-neutral-800">Add integration</h3>
              <div className="grid grid-cols-1 gap-3">
                <div className="space-y-2">
                  <label className="block text-xs sm:text-sm font-medium text-neutral-700">
                    Integration name
                  </label>
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="e.g. slack"
                    className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-800"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-neutral-700">
                    Configuration JSON
                  </label>
                  <JsonEditor value={newConfig} onChange={setNewConfig} height={160} />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-xs text-neutral-500">
                  Use uppercase keys such as{' '}
                  <code className="font-mono">ACTION_SLACK_WEBHOOK_URL</code>. Secret values are
                  encrypted at rest.
                </p>
                <button
                  type="button"
                  className="inline-flex items-center rounded-md bg-neutral-900 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-800 focus:outline-none focus:ring-2 focus:ring-neutral-500"
                  onClick={handleCreate}
                  disabled={updateIntegration.isLoading}
                >
                  {updateIntegration.isLoading ? 'Saving…' : 'Save integration'}
                </button>
              </div>
              {newError ? <p className="text-xs text-red-600">{newError}</p> : null}
            </div>
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-neutral-800">Existing integrations</h3>
              {integrations.isLoading ? (
                <div className="text-sm text-neutral-500">Loading integration settings…</div>
              ) : integrations.data && integrations.data.length > 0 ? (
                <div className="space-y-4">
                  {integrations.data.map((entry) => (
                    <IntegrationCard
                      key={entry.integration}
                      entry={entry}
                      onSave={handleSaveExisting}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-sm text-neutral-500">
                  No integrations configured yet. Add an integration above to store credentials.
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};
